import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Mirrors the mobile app's `apps/mobile/features/tally/theme.ts`.
 * Five editorial light palettes (no dark mode) + an independent
 * ink-vs-accent toggle for primary buttons.
 *
 * The choice is applied to `<html>` via two data attributes:
 *
 *   data-theme="<themeName>"
 *   data-primary-style="ink" | "accent"
 *
 * `global.css` does the rest: each `[data-theme="..."]` block sets
 * the per-palette CSS variables, and `[data-primary-style="accent"]`
 * promotes `--primary` from `--ink` to `--accent`. Components that
 * read shadcn aliases (`bg-primary`, `text-primary-foreground`,
 * `bg-accent`, ...) update without touching component code.
 */
export type ThemeName = 'editorial' | 'harbor' | 'apothecary' | 'pearl' | 'amber';
export type PrimaryStyle = 'ink' | 'accent';

export interface ThemeMeta {
  name: ThemeName;
  label: string;
  description: string;
  /**
   * Mini-preview swatches the picker renders so the user can see
   * what they're choosing before applying. These are pure colours
   * (NOT CSS variables) — the picker draws inline `<div>` swatches
   * that paint the candidate palette regardless of which theme is
   * currently active.
   */
  preview: { bg: string; surface: string; ink: string; accent: string };
}

export const THEMES: Record<ThemeName, ThemeMeta> = {
  editorial: {
    name: 'editorial',
    label: 'Editorial',
    description: 'Warm cream + teal accent. Soft, magazine-like.',
    preview: { bg: '#FAFAF7', surface: '#FFFFFF', ink: '#1A1612', accent: '#0EA5A5' },
  },
  harbor: {
    name: 'harbor',
    label: 'Harbor',
    description: 'Cool light surface with corporate blue accent. Familiar, professional, calm.',
    preview: { bg: '#F4F7FB', surface: '#FFFFFF', ink: '#0F2547', accent: '#2563EB' },
  },
  apothecary: {
    name: 'apothecary',
    label: 'Apothecary',
    description: 'Pale sage paper, deep olive ink, warm mustard accent. Herbalist luxury.',
    preview: { bg: '#EFF1E8', surface: '#F8F8F1', ink: '#1F2A12', accent: '#A87521' },
  },
  pearl: {
    name: 'pearl',
    label: 'Pearl',
    description: 'Cool pearl surfaces, refined indigo accent. Modern, executive, breathable.',
    preview: { bg: '#F2F4FA', surface: '#FFFFFF', ink: '#1B2236', accent: '#6266CF' },
  },
  amber: {
    name: 'amber',
    label: 'Amber',
    description: 'Warm paper cream + amber accent. Considered, grown-up, slightly literary.',
    preview: { bg: '#F0EEE6', surface: '#FAF9F5', ink: '#28231D', accent: '#D77757' },
  },
};

export const THEME_NAMES = Object.keys(THEMES) as ThemeName[];

const DEFAULT_THEME: ThemeName = 'editorial';
const DEFAULT_PRIMARY_STYLE: PrimaryStyle = 'ink';

const THEME_STORAGE_KEY = 'is.web.theme';
const PRIMARY_STYLE_STORAGE_KEY = 'is.web.primaryStyle';

/**
 * One-shot rename map for theme ids that have been removed or
 * renamed. Mirrors the mobile rename map so a user who picks the
 * old name on one device sees the right palette on the other after
 * we ship a rename. Add a row whenever a theme id changes; never
 * remove rows.
 */
const RENAMED_THEMES: Record<string, ThemeName> = {
  // 'claude' was renamed to 'amber' in the mobile app (May 2026);
  // mirror that on web so users coming from the renamed mobile
  // value land on the right palette.
  claude: 'amber',
};

interface ThemeContextValue {
  theme: ThemeName;
  primaryStyle: PrimaryStyle;
  meta: ThemeMeta;
  setTheme: (next: ThemeName) => void;
  setPrimaryStyle: (next: PrimaryStyle) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readPersistedTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    if (raw in THEMES) return raw as ThemeName;
    const renamed = RENAMED_THEMES[raw];
    if (renamed && renamed in THEMES) {
      // Self-heal the storage so the rename map stays a one-time
      // path; the next read short-circuits straight to `in THEMES`.
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, renamed);
      } catch {
        /* ignore */
      }
      return renamed;
    }
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function readPersistedPrimaryStyle(): PrimaryStyle {
  if (typeof window === 'undefined') return DEFAULT_PRIMARY_STYLE;
  try {
    const raw = window.localStorage.getItem(PRIMARY_STYLE_STORAGE_KEY);
    if (raw === 'accent' || raw === 'ink') return raw;
    return DEFAULT_PRIMARY_STYLE;
  } catch {
    return DEFAULT_PRIMARY_STYLE;
  }
}

function applyTheme(theme: ThemeName, primaryStyle: PrimaryStyle) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.primaryStyle = primaryStyle;
  // All palettes are light. Tell the browser so form controls and
  // scrollbars match.
  root.style.colorScheme = 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => readPersistedTheme());
  const [primaryStyle, setPrimaryStyleState] = useState<PrimaryStyle>(
    () => readPersistedPrimaryStyle(),
  );

  useEffect(() => {
    applyTheme(theme, primaryStyle);
  }, [theme, primaryStyle]);

  const setTheme = useCallback((next: ThemeName) => {
    if (!(next in THEMES)) return;
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* persistence is best-effort */
    }
  }, []);

  const setPrimaryStyle = useCallback((next: PrimaryStyle) => {
    setPrimaryStyleState(next);
    try {
      window.localStorage.setItem(PRIMARY_STYLE_STORAGE_KEY, next);
    } catch {
      /* persistence is best-effort */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      primaryStyle,
      meta: THEMES[theme],
      setTheme,
      setPrimaryStyle,
    }),
    [theme, primaryStyle, setTheme, setPrimaryStyle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be called inside <ThemeProvider>.');
  }
  return ctx;
}

/**
 * Synchronous boot script — must run BEFORE React hydrates so the
 * first paint already has the right `data-theme` attribute on
 * `<html>`. Without this users see one frame of editorial cream
 * before their picked palette settles in. Call from `main.tsx`
 * before anything React-related.
 */
export function bootResolveTheme(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  applyTheme(readPersistedTheme(), readPersistedPrimaryStyle());
}
